import {api} from './api';

export interface BacktestTaskRequest {
    taskName: string;
    strategyConfigId: number;
    timeRange: string;
    timeframe: string;
}

export interface BacktestTaskResponse {
    id: number;
    taskName: string;
    freqtradeConfigId: number;
    freqtradeConfigName?: string;
    strategyConfigId: number;
    strategyName?: string;
    timeRange: string;
    timeframe: string;
    status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED';
    errorMsg?: string;
    createdAt?: string;
    finishedAt?: string;

    totalProfitAbs?: number;
    totalProfitPct?: number;
    maxDrawdownAbs?: number;
    maxDrawdownPct?: number;
    winRate?: number;
    totalTrades?: number;
}

export interface BacktestResultResponse {
    taskId: number;
    totalProfitAbs: number;
    totalProfitPct: number;
    maxDrawdownAbs: number;
    maxDrawdownPct: number;
    winRate: number;
    totalTrades: number;
    sharpeRatio?: number;
    strategyName?: string;
    freqtradeConfigName?: string;
    timeRange?: string;
    timeframe?: string;
    dailyProfit: any[];
    trades: any[];
    logs: string;
    // 当前持仓（后端计算）
    currentPositionAmount?: number; // 持仓数量（BTC）
    currentPositionValue?: number;   // 持仓价值（USDT）
    // BTC/ETH持仓收益对比数据
    btcHoldingProfit?: HoldingProfitData[];
    ethHoldingProfit?: HoldingProfitData[];
    // 回测报告表格（markdown格式）
    backtestReportTable?: string;
}

// BTC/ETH持仓收益数据结构
export interface HoldingProfitData {
    date: string;
    price: number;
    cumulativeProfitPct: number;
    cumulativeProfitAbs: number;
}

// 回测AI报告数据结构
export interface BacktestAiReportResponse {
    id: number;
    taskId: number;
    reportContent: string;
    statsJson: string;
    modelName: string;
    createdAt: string;
}

export interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

class BacktestService {
    async createBacktest(request: BacktestTaskRequest): Promise<number> {
        const response = await api.post('/api/v1/backtests', request);
        return response.data.data;
    }

    async getBacktests(status?: string, page: number = 0, size: number = 10): Promise<PageResponse<BacktestTaskResponse>> {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        params.append('page', page.toString());
        params.append('size', size.toString());

        const response = await api.get(`/api/v1/backtests?${params.toString()}`);
        return response.data.data;
    }

    async getBacktestResult(id: number): Promise<BacktestResultResponse> {
        const response = await api.get(`/api/v1/backtests/${id}/result`);
        return response.data.data;
    }

    async stopBacktest(id: number): Promise<void> {
        await api.post(`/api/v1/backtests/${id}/stop`);
    }

    async pauseBacktest(id: number): Promise<void> {
        await api.post(`/api/v1/backtests/${id}/pause`);
    }

    async resumeBacktest(id: number): Promise<void> {
        await api.post(`/api/v1/backtests/${id}/resume`);
    }

    async deleteBacktest(id: number): Promise<void> {
        await api.delete(`/api/v1/backtests/${id}`);
    }

    async validateTaskName(taskName: string, excludeId?: number): Promise<boolean> {
        const params = new URLSearchParams();
        params.append('taskName', taskName);
        if (excludeId) params.append('excludeId', excludeId.toString());
        const response = await api.get(`/api/v1/backtests/validate-name?${params.toString()}`);
        return response.data.data;
    }

    async getBacktestAiReport(id: number): Promise<BacktestAiReportResponse | null> {
        const response = await api.get(`/api/v1/backtests/${id}/ai-report`);
        return response.data.data;
    }
}

export const backtestService = new BacktestService();
